import React, {useMemo, useReducer, useState} from "react";
import {parseISO, startOfMonth} from "date-fns";
import * as Sentry from "@sentry/react";

import {useMainLayout} from "../../../layouts/Main";
import Planner from "../../../components/Planner";
import useDialog from "../../../hooks/useDialog";
import FormRate, {ARRIVAL_DEPARTURE_TAB, BASE_TAB, NIGHTS_TAB, OCCUPANCY_TAB} from "../FormRate";
import QueryMonthDataLoader from "./QueryMonthDataLoader";
import usePriceSummarizer from "../FormRate/usePriceSummarizer";
import {
    extractRateId,
    getResources, isBaseResource,
    isCheckinResource, isCheckoutResource, isMinNightsResource,
    isMinPeopleResource,
    isPeopleResource
} from "./calendarResourceUtils";
import {useApartmentRatesQuery} from "../query";
import FormConfigurator from "../FormConfigurator";
import NoPriceDialog from "../NoPriceDialog";
import MobilePlanner from "./MobilePlanner";
import DesktopPlanner from "./DesktopPlanner";
import UseNoPriceDialog from "../NoPriceDialog/useNoPriceDialog";
import {getLastDateToPlan} from "../index";

import Container from "@mui/material/Container";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import {useRates} from "../RateContext";


/*
 * Format the given price according to the given mode. It assumes the given price to be
 * in price per apartment format.
 */
export const formatPriceCent = (priceCent, people, mode = 'person') => {
    const formattedPriceCent = (priceCent / 100 / (mode === 'person' ? people : 1))
    return formattedPriceCent % 1 === 0 ? formattedPriceCent : formattedPriceCent.toFixed(1);
}

/*
 * Parse the given price with the given mode and return the priceCent per apartment.
 */
export const parsePrice = (price, people, mode = 'person') => {
    return price * 100 * (mode === 'person' ? people : 1);
}

const RatePlanner = () => {
    // State and context
    const {accUnitSelector} = useRates();
    const selectedApartment = accUnitSelector.selectedAccommodationUnit;
    const {isMobile} = useMainLayout();
    const [rateSummary, summaryFromQueryCache, resetRateSummary] = usePriceSummarizer();

    const formRate = useDialog();
    const formConfigurator = useDialog();
    const [formSetting, setFormSetting] = useState({rate: null, dateFromISO: null, dateToISO: null, tab: 'base', dateDisabled: true});

    const initialDate = useMemo(() => new Date(), []);
    const minDate = useMemo(() => startOfMonth(new Date()), []);
    const maxDate = useMemo(() => getLastDateToPlan(), []); // eslint-disable-line react-hooks/exhaustive-deps

    const [expanded, toggleExpanded] = useReducer(oldState => !oldState, false);  // TODO allow multiple rated to be expanded
    const [ratesViewMode, setRatesViewMode] = React.useState('person');

    // Queries
    const ratesQuery = useApartmentRatesQuery(selectedApartment?.id);

    const baseRate = ratesQuery.data?.find(rate => rate.isBase === true);
    const ratesLoaded = ratesQuery.isSuccess && !!ratesQuery.data;

    if (ratesLoaded && !baseRate) {
        console.warn(`No base rate found for apartment id=${selectedApartment.id}`);
        Sentry.captureMessage(`No base rate found for apartment id=${selectedApartment.id}`);
    }

    // No price dialog (when no price is found for the selected period)
    const noPricesDialog = UseNoPriceDialog({
        enabled: !formRate.show && !formConfigurator.show,
        apartmentId: selectedApartment?.id,
        rate: baseRate});

    const setUpSelectedData = (dateFromISO, dateToISO, resourceId) => {
        const rateId = extractRateId(resourceId);
        const rate = ratesQuery.data.find(rate => rate.id.toString() === rateId.toString());

        if (!rate) {
            Sentry.captureMessage(`Rate id not found rateId=${rateId}`);
        }

        summaryFromQueryCache(selectedApartment?.id, rateId, parseISO(dateFromISO), parseISO(dateToISO));

        let tab = BASE_TAB;
        if (isPeopleResource(resourceId) || isMinPeopleResource(resourceId)) {
            tab = OCCUPANCY_TAB;
        } else if (isCheckinResource(resourceId) || isCheckoutResource(resourceId)) {
            tab = ARRIVAL_DEPARTURE_TAB;
        } else if (isMinNightsResource(resourceId)) {
            tab = NIGHTS_TAB;
        }
        // date are disabled when selecting because the filled values (obtained with the summaryFromQueryCache)
        // depends on the dates. Changing them make these values invalid
        setFormSetting({rate, dateFromISO, dateToISO, tab, dateDisabled: true});
    }

    // Handlers
    const handleRatesViewMode = (event, newViewMode) => {
        if (newViewMode !== null) {
            setRatesViewMode(newViewMode);
        }
    };

    /*
     * Set the formSetting state with the given values and open the FormRate dialog
     * if a constraint is selected.
     */
    const handleSelect = (dateFromISO, dateToISO, resourceId) => {
        const selectedPrice = isBaseResource(resourceId) || isPeopleResource(resourceId);
        setUpSelectedData(dateFromISO, dateToISO, resourceId);

        if (!selectedPrice) {
            formRate.open();
        }
    };

    /*
     * Load the summary and open the FormRate dialog.
     * Reloading the summary is necessary when the user edit the price then close the dialog and reopen it
     * with the same selection
     */
    const handleEdit = (dateFromISO, dateToISO, resourceId) => {
        setUpSelectedData(dateFromISO, dateToISO, resourceId);
        formRate.open();
    }

    /*
     * Open the FormRate dialog with no pre-filled values
     */
    const handleMassEdit = () => {
        // TODO allow to select another rate other than the base rate
        setFormSetting({rate: baseRate, dateFromISO: null, dateToISO: null, tab: 'base', dateDisabled: false});
        resetRateSummary();
        formRate.open()
    }

    const resources = useMemo(() => {
            return ratesQuery.isLoading ?
                [{resourceId: "loading", renderLabel: () => <Stack direction="row" alignItems="center" spacing={1}>
                        <CircularProgress color="primary" size={14}/>
                        <Typography>
                            Caricamento...
                        </Typography>
                </Stack>}]
                :
                getResources(ratesQuery.data, expanded, toggleExpanded, ratesViewMode);
        },
        [expanded, ratesViewMode, ratesQuery.isLoading, ratesQuery.data]);

    return <Container disableGutters={isMobile}
                      maxWidth={false}
                      sx={{display: 'flex', flexDirection: 'column', flexGrow: 1}}>

        <Planner minDate={minDate}
                 maxDate={maxDate}
                 initialDate={initialDate}
                 resources={resources}
                 onSelect={handleSelect}
                 MonthDataLoader={QueryMonthDataLoader}>
            {
                isMobile ?
                    <MobilePlanner baseRate={baseRate}
                                   formConfigurator={formConfigurator}/>
                    :
                    <DesktopPlanner baseRate={baseRate}
                                    ratesViewMode={ratesViewMode}
                                    onChangeRatesViewMode={handleRatesViewMode}
                                    onClickMassEdit={handleMassEdit}
                                    onPriceEdit={handleEdit}
                                    formConfigurator={formConfigurator}/>
            }
        </Planner>

        {formRate.show && ratesLoaded && <FormRate onClose={formRate.close}
                                                   rate={formSetting.rate}
                                                   apartmentId={selectedApartment?.id}
                                                   dateFromISO={formSetting.dateFromISO}
                                                   dateToISO={formSetting.dateToISO}
                                                   tab={formSetting.tab}
                                                   initialValues={rateSummary}
                                                   ratesViewMode={ratesViewMode}
                                                   onRatesViewModeChange={handleRatesViewMode}
                                                   dateDisabled={formSetting.dateDisabled}/>}

        {formConfigurator.show && ratesLoaded && <FormConfigurator onClose={formConfigurator.close}
                                                                   apartmentId={selectedApartment?.id}
                                                                   rate={baseRate}  // TODO allow to select the base rate
        />}
        {noPricesDialog.show && <NoPriceDialog onClose={noPricesDialog.close}
                                               openConfigurator={formConfigurator.open}/>}
    </Container>
}

export default RatePlanner;